import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { partiesApi, type PaginatedPartiesResponse } from "@/lib/api";
import type { Party } from "@/types";
import { useOrganizationStore } from "@/stores/organizationStore";

import { DEFAULT_PAGE_SIZE } from "@/lib/constants";

interface UsePartiesOptions {
  enabled?: boolean;
  type?: "artist" | "label" | "organization";
  search?: string;
  country?: string;
  pageSize?: number;
  page?: number;
  ordering?: string;
}

interface UsePartiesReturn {
  parties: Party[];
  count: number;
  next: string | null;
  previous: string | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isSuccess: boolean;
  refetch: () => void;
}

// Helper function to create normalized query keys
const createPartiesQueryKey = (
  labelId: string | undefined,
  type: string | undefined,
  search: string | undefined,
  country: string | undefined,
  pageSize: number | undefined,
  page: number | undefined,
  ordering: string | undefined,
) => {
  const key: (string | number | boolean)[] = ["parties"];

  // Always include labelId (required for most queries)
  if (labelId) key.push("label", labelId);

  // Add type if specified
  if (type) key.push("type", type);

  // Normalize search - convert undefined/empty to consistent value
  const normalizedSearch = search?.trim() || null;
  if (normalizedSearch) key.push("search", normalizedSearch);

  // Add country if specified
  if (country) key.push("country", country);

  // Add pageSize if specified and different from default
  if (pageSize && pageSize !== DEFAULT_PAGE_SIZE) key.push("pageSize", pageSize);

  if (page && page > 1) key.push("page", page);

  if (ordering) key.push("ordering", ordering);

  return key;
};

export const useParties = ({
  enabled = true,
  type,
  search,
  country,
  pageSize,
  page,
  ordering,
}: UsePartiesOptions = {}): UsePartiesReturn => {
  const { currentLabel } = useOrganizationStore();

  const filters = {
    label_id: currentLabel?.id,
    ...(type && { type }),
    ...(search && { q: search }),
    ...(country && { country }),
    ...(pageSize && { page_size: pageSize }),
    ...(page && { page }),
    ...(ordering && { ordering }),
  };

  // Use normalized query key
  const queryKey = createPartiesQueryKey(
    currentLabel?.id,
    type,
    search,
    country,
    pageSize,
    page,
    ordering,
  );

  const { data, isLoading, isError, error, isSuccess, refetch } = useQuery({
    queryKey,
    queryFn: async (): Promise<PaginatedPartiesResponse> => {
      return await partiesApi.getParties(filters);
    },
    enabled: enabled && !!currentLabel,
    placeholderData: keepPreviousData,
  });

  return {
    parties: data?.results ?? [],
    count: data?.count ?? 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
    isLoading,
    isError,
    error,
    isSuccess,
    refetch,
  };
};
